﻿import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { str_isEmpty } from '@Util/Utils';
import dayjs from 'dayjs';
import { dashboard_pages } from '@Util/GlobalValues';
import { Tooltip } from 'react-tooltip';
import relativeTime from 'dayjs/plugin/relativeTime';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';

import { 
    Client_Websites_Errors_Table_Props, 
    Client_Website_Warning_Type,
    Client_Website_Type 
} from '@Util/Types';

dayjs.extend(relativeTime);

export default function Client_Websites_Errors_Table(props: Client_Websites_Errors_Table_Props): JSX.Element {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("name");

    const table_data: Client_Website_Warning_Type[] = useMemo(() => {
        let sorted_data: Client_Website_Warning_Type[] = [];

        if (sort_direction == "Ascending") {
            sorted_data = props.data.sort((a: Client_Website_Warning_Type, b: Client_Website_Warning_Type) => a[active_sorting_column] > b[active_sorting_column] ? 1 : -1);
        } else {
            sorted_data = props.data.sort((a: Client_Website_Warning_Type, b: Client_Website_Warning_Type) => a[active_sorting_column] < b[active_sorting_column] ? 1 : -1);
        }

        return sorted_data;
    }, [props.data, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function find_and_return_customer_name(website_id: string): string {
        const client = props.client_websites.find((customer_website: Client_Website_Type) => customer_website.id === website_id);
        
        if (client) {
            return client.name;
        }

        return t('Text.Unknown');
    }

    function create_warning_value_column(website: Client_Website_Warning_Type): JSX.Element {
        const tooltip_id: string = `client_website_error_tooltip_${website.website_id}`;
        let description_text: string = "";
        let value_text: string = "";
        
        switch (website.warning_type) {
            case "response_time":
                description_text = t('Text.HighResponseTime');
                value_text = `${typeof website.warning_value === "number" ? website.warning_value.toFixed(1) : website.warning_value} ms`;
                break;
            case "exception_count":
                description_text = t('Text.HighExceptionsRate');
                value_text = `${website.warning_value} st`;
                break;
            case "ssl_expiration":
                description_text = t('Text.SSLExpirationSoon');
                value_text = `${dayjs(website.warning_value).format('YYYY-MM-DD')}`;
                break;
        }

        return (
            <div className="Table_Data_Client_Website_Error_Description">
                <a
                    data-tooltip-id={tooltip_id}
                    data-tooltip-content={value_text}
                    data-tooltip-place="top"
                >
                    {description_text}
                </a>
                <Tooltip id={tooltip_id} />
            </div>
        )
    }

    function handle_row_click(website: Client_Website_Warning_Type): void {
        navigate(dashboard_pages.customer_websites + "/" + website.website_id);
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoErrorsToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    return (
        <>
            <div className="Table_Wrapper Table_Wrapper--NoMinWidth">
                <table className={`Table ${!table_data.isEmpty() && "Table--Anchored"}`}>
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Customer')}
                                sortingAction={() => handleSetSortingOnTableHead('website_id')}
                                sortingDataName="website_id"
                                style={{ width: "40%" }}
                            />
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Value')}
                                sortingAction={() => handleSetSortingOnTableHead('warning_value')}
                                sortingDataName="warning_value"
                                style={{ width: "25%" }}
                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.LastUpdated')}
                                sortingAction={() => handleSetSortingOnTableHead('warning_time')}
                                sortingDataName="warning_time"
                                style={{ width: "35%" }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.isEmpty() ?
                                <Empty_Table /> :
                                table_data.map((website: Client_Website_Warning_Type , index: number) =>
                                    <tr 
                                        key={`client_website_error_table_row${index}`} 
                                        onClick={() => handle_row_click(website)}
                                    >
                                        <Table_Data
                                            align="left"
                                            style={{ width: "30%" }}
                                        >
                                            {find_and_return_customer_name(website.website_id)}
                                        </Table_Data>
                                        <Table_Data
                                            align="left"
                                            style={{ width: "35%" }}
                                        >
                                            {create_warning_value_column(website)}
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            style={{ width: "35%" }}
                                        >
                                            <div
                                                data-tooltip-id={`${website.website_id}-${index}`}
                                                data-tooltip-content={dayjs(website.warning_time).format('YYYY-MM-DD')}
                                                data-tooltip-place="top"
                                            >
                                                {dayjs(website.warning_time).fromNow(true)}
                                                <Tooltip id={`${website.website_id}-${index}`} />
                                            </div>
                                        </Table_Data>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
        </>
    );
}